import { useRef } from "react";
import { useQuery } from "react-query";

import { QUERY_KEY_STRINGS, SpotterMapData } from "../shared-types";
import { LatestSpotterWaveData } from "../shared-types/SpotterTypes";

import config, { stormGlassAPIToken } from "../config";
import { isNumber } from "./units";
import AsyncWeatherProviderProxy from "web-workers/weather-worker/AsyncWeatherProviderProxy";

const fetchAllLatestSpotterData = async (staleSpotterDataAgeMs: number) => {
  const latestData = (
    await (
      await fetch(config.spotterLatestDataEndpoint, {
        headers: new Headers({ token: stormGlassAPIToken }),
      })
    ).json()
  ).data;
  // at some point we should probably limit this to only certain spotters
  const getSpotterMapData = (
    spotter: LatestSpotterWaveData
  ): SpotterMapData => {
    // only get one sample for now
    let lastSample = spotter.waves.length - 1;
    let waves = spotter.waves[lastSample];
    let wind = spotter.wind[lastSample];
    // if possible, find the latest point that does not have missing coordinates
    while (
      (!isNumber(waves.latitude) || !isNumber(waves.longitude)) &&
      lastSample > 0
    ) {
      lastSample--;
      waves = spotter.waves[lastSample];
      wind = spotter.wind[lastSample];
    }
    return {
      // metadata for convenience
      position: {
        lat: waves.latitude,
        lon: waves.longitude,
      },
      label: spotter.spotterId,
      id: spotter.spotterId,
      timestamp: waves.timestamp,
      // add data from spotter
      wind: wind ? { ...wind } : undefined,
      waves: { ...waves }, // `waves` contains the authoritative position and timestamp
    };
  };
  return Object.keys(latestData)
    .map((id: string) => latestData[id])
    .filter((spotter) => spotter.waves && spotter.waves.length)
    .map(getSpotterMapData)
    .filter((s) => isNumber(s.position.lat) && isNumber(s.position.lon))
    .filter((d) => {
      const dataAgeMs = new Date().getTime() - new Date(d.timestamp).getTime();
      return d.timestamp && dataAgeMs < staleSpotterDataAgeMs;
    });
};

/** Hook for use by any component that needs to consumer Spotter data.
 *  This hook is backed by `react-query` so it can safely be re-used in many
 *  components and `react-query` will cache and dedupe requests for us.
 */
const useAllSpotterData = () => {
  const { staleSpotterDataAgeMs } = config;
  const staleAge = useRef(staleSpotterDataAgeMs);
  const {
    status,
    data: allSpotterData,
    error,
  } = useQuery(
    // `react-query` will automatically de-dupe and cache results for all
    // requests that share this query key
    QUERY_KEY_STRINGS.SPOTTER_REALTIME,
    () => fetchAllLatestSpotterData(staleAge.current),
    {
      // Automatically re-fetch Spotter data according to this interval.
      // `react-query` automatically handles the start/stop poll behavior for
      // us, so this will only poll when at least one component consuming
      // this query is mounted, and it will poll at most once regardless of
      // how many components consuming this query are mounted.
      refetchInterval: config.spotterPollIntervalMs,

      // Don't re-fetch data ever unless it is older than this amount of time.
      // `staleTime` defaults to 0, which means cached data will be used in
      // subsequent requests but, in parallel, new data will be fetched as
      // well. This is undesireable in the case of Spotter data, because we
      // use the same query for getting all Spotter data as well as rendering
      // data in a single Spotter Inspector component— we don't want the
      // inspector to casue a re-fetch of all Spotter data every time the user
      // clicks on a new inspector.
      staleTime: config.spotterPollIntervalMs - 1,
    }
  );

  // If we only want one Spotter from the list, do the filtering here
  return { status, error, data: allSpotterData };
};

/** Hook used to fetch a single Spotter's data.
 *  This is backed by the same query and cache as `useAllSpotterData` so it
 *  should not re-fetch data if it has already cached data for all Spotters.
 */
const useSingleSpotterData = (spotterID: string | undefined) => {
  const allSpotters = useAllSpotterData();

  if (spotterID === undefined) return undefined;

  return {
    ...allSpotters,
    data: allSpotters.data?.find((s) => s.id === spotterID),
  };
};

const getSingleSpotterWaveData = async (
  spotterID: string | undefined,
  getRequestTimes: AsyncWeatherProviderProxy["getRequestTimes"]
) => {
  if (!spotterID) return;

  const getTimes = async () => await getRequestTimes();

  return getTimes().then((requestTimes) => {
    if (!requestTimes) return;

    const localStartTime = requestTimes.requestedStartTime.toISOString();
    const localEndTime = requestTimes.requestedEndTime.toISOString();

    const spotterURL = config.spotterWaveDataEndpoint(
      spotterID,
      localStartTime,
      localEndTime
    );
    return fetch(spotterURL, {
      headers: new Headers({ token: stormGlassAPIToken }),
    }).then((result) => result.json());
  });
};

export { useAllSpotterData, useSingleSpotterData, getSingleSpotterWaveData };
